<template>
  <article class="invoice-doc" :class="{ preview }">
    <header class="inv-head">
      <div>
        <p class="brand">{{ contractorName || issuer.name || 'OilBuddy' }}</p>
        <p class="muted">Contractor invoice</p>
        <p v-if="issuer.email" class="muted">{{ issuer.email }}</p>
      </div>
      <div class="inv-meta">
        <h1>INVOICE</h1>
        <p class="num">{{ invoice.invoiceNumber || '—' }}</p>
        <p class="status" :class="'st-' + String(invoice.status || 'pending').toLowerCase()">
          {{ invoice.status || 'pending' }}
        </p>
      </div>
    </header>

    <section class="facts">
      <div>
        <h2>Bill To</h2>
        <p class="strong">{{ billTo }}</p>
      </div>
      <div v-if="isFieldInvoice">
        <h2>Contractor</h2>
        <p class="strong">{{ contractorName || '—' }}</p>
      </div>
      <div v-if="isFieldInvoice">
        <h2>Well Name / Location</h2>
        <p class="strong">{{ wellLine }}</p>
      </div>
      <div class="dates">
        <div><span>Issued</span><b>{{ formatDate(invoice.issuedDate) }}</b></div>
        <div><span>Due</span><b>{{ formatDate(invoice.dueDate) }}</b></div>
        <div v-if="!isFieldInvoice && (invoice.jobId || invoice.jobTitle)">
          <span>Job</span><b>{{ invoice.jobId || invoice.jobTitle }}</b>
        </div>
      </div>
      <div v-if="isFieldInvoice">
        <h2>Day Rate</h2>
        <p class="strong">${{ money(dayRate) }} <span class="per">/ day</span></p>
      </div>
      <div v-if="isFieldInvoice">
        <h2>Days Worked</h2>
        <p class="strong">{{ daysLabel }}</p>
      </div>
    </section>

    <table class="lines">
      <thead>
        <tr>
          <th>Description</th>
          <th class="num">{{ isFieldInvoice ? 'Days' : 'Qty' }}</th>
          <th>Unit</th>
          <th class="num">{{ isFieldInvoice ? 'Day Rate' : 'Rate' }}</th>
          <th class="num">Amount</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="(line, i) in lines" :key="line.id || i">
          <td>{{ line.description || 'Day rate' }}</td>
          <td class="num">{{ line.quantity ?? daysWorked ?? 1 }}</td>
          <td>{{ line.unit || 'DAY' }}</td>
          <td class="num">${{ money(line.rate ?? dayRate) }}</td>
          <td class="num">${{ money(line.amount ?? Number(line.quantity || 1) * Number(line.rate || 0)) }}</td>
        </tr>
      </tbody>
    </table>

    <div class="totals">
      <div>
        <span>Total Due</span>
        <strong>${{ money(invoice.amount) }}</strong>
      </div>
    </div>

    <p v-if="invoice.notes" class="notes">{{ invoice.notes }}</p>
    <footer class="inv-foot">Thank you for your business.</footer>
  </article>
</template>

<script setup lang="ts">
import { computed } from 'vue'

const props = defineProps<{
  invoice: Record<string, any>
  issuer?: { name?: string; company?: string; email?: string }
  preview?: boolean
}>()

const issuer = computed(() => props.issuer || {})

const billTo = computed(() =>
  props.invoice?.billTo
  || props.invoice?.clientCompany
  || props.invoice?.clientName
  || props.invoice?.client
  || 'Company',
)

const isFieldInvoice = computed(() => !!(
  props.invoice?.contractorName
  || props.invoice?.wellName
  || Number(props.invoice?.dayRate || 0) > 0
  || Number(props.invoice?.daysWorked || 0) > 0
))

const contractorName = computed(() =>
  props.invoice?.contractorName || (isFieldInvoice.value ? (issuer.value.name || '') : ''),
)

const wellName = computed(() => props.invoice?.wellName || props.invoice?.jobTitle || '')
const location = computed(() => props.invoice?.wellLocation || props.invoice?.location || '')
const wellLine = computed(() => {
  const well = String(wellName.value || '').trim()
  const loc = String(location.value || '').trim()
  if (well && loc && loc !== well) return `${well} — ${loc}`
  return well || loc || '—'
})

const dayRate = computed(() => {
  const stored = Number(props.invoice?.dayRate || 0)
  if (stored > 0) return stored
  const first = (props.invoice?.lines || [])[0]
  return Number(first?.rate || props.invoice?.amount || 0)
})

const daysWorked = computed(() => {
  const stored = Number(props.invoice?.daysWorked || 0)
  if (stored > 0) return stored
  const first = (props.invoice?.lines || [])[0]
  return Number(first?.quantity || 1)
})

const daysLabel = computed(() => {
  const days = daysWorked.value
  const text = Number.isInteger(days) ? String(days) : String(days)
  return `${text} ${days === 1 ? 'day' : 'days'}`
})

const lines = computed(() => {
  const raw = props.invoice?.lines || props.invoice?.line_items || []
  if (Array.isArray(raw) && raw.length) return raw
  return [{
    description: wellName.value ? `Day rate — ${wellName.value}` : 'Day rate',
    quantity: daysWorked.value || 1,
    unit: 'DAY',
    rate: dayRate.value,
    amount: props.invoice?.amount || 0,
  }]
})

function money(value: unknown) {
  return Number(value || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

function formatDate(value?: string) {
  if (!value) return '—'
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return value
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
}
</script>

<style scoped>
.invoice-doc {
  background: #fff;
  color: #0f172a;
  padding: 32px 36px;
  border-radius: 8px;
  font-size: 13px;
  line-height: 1.45;
}
.invoice-doc.preview {
  box-shadow: 0 18px 40px rgba(0, 0, 0, 0.35);
}
.inv-head {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  border-bottom: 2px solid #0f172a;
  padding-bottom: 16px;
}
.brand { font-size: 20px; font-weight: 800; letter-spacing: 0.02em; }
.muted { color: #475569; margin-top: 2px; }
.inv-meta { text-align: right; }
.inv-meta h1 { font-size: 28px; letter-spacing: 0.12em; margin: 0; }
.num { font-family: ui-monospace, SFMono-Regular, Menlo, monospace; }
.status {
  display: inline-block;
  margin-top: 8px;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  background: #fef3c7;
  color: #92400e;
}
.st-paid { background: #d1fae5; color: #065f46; }
.st-cancelled, .st-overdue { background: #fee2e2; color: #991b1b; }
.st-sent { background: #dbeafe; color: #1e3a8a; }
.facts {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 18px 24px;
  margin: 20px 0 18px;
}
.facts h2 {
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #64748b;
  margin-bottom: 6px;
}
.strong { font-weight: 700; font-size: 15px; }
.per { font-size: 12px; font-weight: 500; color: #64748b; }
.dates div { display: flex; justify-content: space-between; gap: 16px; margin-bottom: 4px; }
.dates span { color: #64748b; }
.lines { width: 100%; border-collapse: collapse; }
.lines th, .lines td { padding: 8px 6px; border-bottom: 1px solid #e2e8f0; text-align: left; }
.lines th { font-size: 11px; text-transform: uppercase; color: #64748b; }
.lines .num, .lines th.num { text-align: right; }
.totals { display: flex; justify-content: flex-end; margin-top: 16px; }
.totals div {
  min-width: 220px;
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  border-top: 2px solid #0f172a;
  padding-top: 10px;
  font-size: 16px;
}
.notes { margin-top: 22px; color: #475569; white-space: pre-wrap; }
.inv-foot { margin-top: 28px; color: #64748b; font-size: 12px; }

@media print {
  .invoice-doc {
    box-shadow: none;
    border-radius: 0;
    padding: 0;
  }
}
</style>
